/**
 * Screen-share entire-screen detection.
 *
 * The "share your ENTIRE SCREEN" gate used to reject a stream whenever
 * `track.getSettings().displaySurface !== 'monitor'`. That strict equality
 * produced false negatives: on several Chrome builds / multi-monitor /
 * virtualized-display setups `displaySurface` comes back `undefined` (or
 * isn't settled yet) immediately after `getDisplayMedia()` resolves, even
 * when the candidate genuinely picked "Entire screen". The valid stream was
 * then discarded, the candidate saw the toast, the interview gate blocked,
 * and — because the meeting recorder only starts after the gate passes — no
 * `interview_video_chunk` frames were ever produced, so the candidate video
 * was never saved.
 *
 * The lenient check rejects only when the surface is *positively* a window
 * or browser tab; an unknown/unreported surface is accepted.
 */
/** Default-ON flag: enabled unless explicitly set to a falsy value. */
function flagDefaultOn(raw) {
    const v = String(raw !== null && raw !== void 0 ? raw : '')
        .trim()
        .toLowerCase();
    if (v === '')
        return true; // unset ⇒ on by default
    return !(v === '0' || v === 'false' || v === 'no' || v === 'off');
}
/**
 * Lenient entire-screen detection. Default ON; set
 * REACT_APP_LENIENT_SCREEN_SHARE_ENABLED=false to restore the strict
 * `displaySurface === 'monitor'` behavior.
 */
export function isLenientScreenShareEnabled() {
    return flagDefaultOn(process.env.REACT_APP_LENIENT_SCREEN_SHARE_ENABLED);
}
/**
 * True when the captured display surface should be accepted as a full-screen
 * share.
 *
 * - Lenient (default): accept `'monitor'` and any unknown/missing surface;
 *   reject only a positively-reported `'window'` or `'browser'` tab.
 * - Strict (flag off): legacy behavior — accept only `'monitor'`.
 */
export function isEntireScreenShare(surface) {
    if (!isLenientScreenShareEnabled()) {
        return surface === 'monitor';
    }
    // Reject only when the browser positively says it's a window or a tab.
    return surface !== 'window' && surface !== 'browser';
}
export function readCaptureSurface(track) {
    const fallback = { surface: 'unknown', trackW: 0, trackH: 0, trackFps: 0 };
    if (!track || typeof track.getSettings !== 'function')
        return fallback;
    let settings;
    try {
        settings = track.getSettings();
    }
    catch (_a) {
        return fallback;
    }
    const raw = String(settings.displaySurface || '').toLowerCase();
    const surface = raw === 'monitor' || raw === 'window' || raw === 'browser' ? raw : 'unknown';
    return {
        surface,
        // VA-28255 Phase 2: these ARE the recording's coordinate space, and the
        // reason the mapping can stop guessing. `camBox` has been a fraction of
        // `screen.width` — a denominator that only equals the recording when a
        // full monitor is shared by a viewport-filling page. The track reports what
        // was actually captured, so it is the denominator the mapping should use.
        trackW: typeof settings.width === 'number' ? settings.width : 0,
        trackH: typeof settings.height === 'number' ? settings.height : 0,
        trackFps: typeof settings.frameRate === 'number' ? settings.frameRate : 0,
    };
}
